import { useState } from "react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { items, total, clear } = useCart();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [placing, setPlacing] = useState(false);
  const [placedOrder, setPlacedOrder] = useState(null);

  if (loading) return null;
  if (!user) {
    navigate("/auth");
    return null;
  }

  if (items.length === 0 && !placedOrder) {
    navigate("/products");
    return null;
  }

  const place = async () => {
    if (!address.trim() || !phone.trim()) { toast.error("Address and phone required"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        address, phone,
      });
      // MOCK Razorpay verify
      await api.post("/payments/verify", {
        order_id: data.order_id,
        razorpay_payment_id: `pay_mock_${Date.now()}`,
        razorpay_signature: "mock_signature",
      });
      setPlacedOrder({ id: data.order_id, amount: data.amount });
      clear();
      toast.success("Order placed successfully!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally {
      setPlacing(false);
    }
  };

  if (placedOrder) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-16 h-16 text-[#275F36] mx-auto mb-6" />
        <h1 className="text-4xl">Dhanyavaad!</h1>
        <p className="mt-4 text-[#4A5568]">Your order #{placedOrder.id.slice(0, 8)} for ₹{placedOrder.amount.toLocaleString('en-IN')} is confirmed.</p>
        <p className="text-sm text-[#718096] mt-2">Confirmation email has been sent to {user.email}.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => navigate("/orders")} className="btn-primary" data-testid="view-orders-btn">View orders</button>
          <button onClick={() => navigate("/products")} className="btn-outline">Continue shopping</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-8">
      <div>
        <h1 className="text-4xl mb-6">Checkout</h1>
        <div className="card-flat p-6 space-y-4">
          <div>
            <label className="label-caps">Delivery address</label>
            <textarea
              data-testid="checkout-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={3}
              placeholder="House no, street, city, state, pincode"
              className="mt-2 w-full px-3 py-2 border border-[#E2DCD0] rounded-lg text-sm focus:outline-none focus:border-[#E25A24]"
            />
          </div>
          <div>
            <label className="label-caps">Phone</label>
            <input
              data-testid="checkout-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+91 …"
              className="mt-2 w-full px-3 py-2 border border-[#E2DCD0] rounded-lg text-sm focus:outline-none focus:border-[#E25A24]"
            />
          </div>
          <div className="p-4 bg-[#FFF3EA] rounded-lg text-xs text-[#B44618] border border-[#EAB89A]">
            MOCKED Razorpay checkout — payment is simulated. Add live Razorpay keys later to enable real UPI/card payments.
          </div>
        </div>
      </div>
      <div>
        <div className="card-flat p-6">
          <div className="label-caps mb-4">Your order</div>
          {items.map((it) => (
            <div key={it.id} className="flex justify-between text-sm py-2 border-b border-[#E2DCD0] last:border-0">
              <span className="text-[#4A5568]">{it.title} × {it.quantity}</span>
              <span className="font-semibold">₹{(it.price * it.quantity).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div className="flex justify-between items-end mt-6">
            <span className="text-sm text-[#4A5568]">Total</span>
            <span className="text-2xl font-bold">₹{total.toLocaleString('en-IN')}</span>
          </div>
          <button
            data-testid="place-order-btn"
            onClick={place}
            disabled={placing}
            className="mt-6 w-full btn-primary disabled:opacity-50"
          >
            {placing ? "Placing…" : "Pay & place order"}
          </button>
        </div>
      </div>
    </div>
  );
}
